import { create } from "zustand";
import {
  createJSONStorage,
  persist,
  type StateStorage,
} from "zustand/middleware";

/** One column an operator can show or hide. */
export interface ColumnDefinition<TId extends string = string> {
  /** Stable key. It is written to localStorage, so renaming one retires the stored preference. */
  id: TId;

  /** What the menu calls this column. Usually the table header's own text. */
  label: string;

  /**
   * A column that cannot be hidden. For the ones a row is unusable without: the selection
   * checkbox, the name that identifies the row, the actions menu. Listed anyway, greyed out, so
   * the menu is a complete picture of the table rather than a partial one.
   */
  locked?: boolean;

  /**
   * Hidden until an operator asks for it. For columns that are useful occasionally and cost a lot
   * of width — an internal id, a raw timestamp.
   */
  hiddenByDefault?: boolean;
}

export interface ColumnVisibilityState<TId extends string = string> {
  /**
   * The hidden ids, not the visible ones. Deliberate: a column added in a later release is absent
   * from every stored preference, so storing what is HIDDEN makes it appear for everyone, while
   * storing what is VISIBLE would hide it from every operator who ever opened this screen — and
   * they would have no reason to look in a menu for a column they have never seen.
   */
  hidden: TId[];

  isVisible: (id: TId) => boolean;

  /** Shows a hidden column, hides a visible one. No-op for a locked column. */
  toggle: (id: TId) => void;

  /** Brings every column back. */
  showAll: () => void;

  /** Back to what a first-time visitor sees, including any `hiddenByDefault` columns. */
  reset: () => void;

  /** How many columns are hidden right now — for the badge on the menu button. */
  hiddenCount: () => number;
}

export interface CreateColumnVisibilityStoreOptions<TId extends string> {
  /**
   * localStorage key. Convention: `admin.<area>.columns`, named like `admin.<area>.filters` from
   * `createTableFiltersStore` — that one is in sessionStorage: filters are per tab, columns not.
   */
  name: string;

  /** Every column of the table, in the order the menu should list them. */
  columns: readonly ColumnDefinition<TId>[];

  /**
   * Bump when a column id is renamed or removed. Unknown ids are dropped on load anyway, so this
   * is only needed when the MEANING of an id changes — otherwise an operator who hid the old
   * "order" column silently keeps hiding the new one.
   */
  version?: number;
}

/**
 * localStorage that cannot throw — same reasoning as `createTableFiltersStore`: a Safari private
 * window, blocked site data or a full quota is not a reason to take an admin screen down.
 */
const safeLocalStorage: StateStorage = {
  getItem: (name) => {
    try {
      return globalThis.localStorage?.getItem(name) ?? null;
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      globalThis.localStorage?.setItem(name, value);
    } catch {
      // Quota exceeded or storage blocked — the choice holds for this session only.
    }
  },
  removeItem: (name) => {
    try {
      globalThis.localStorage?.removeItem(name);
    } catch {
      // Nothing to recover, nothing worth failing over.
    }
  },
};

/**
 * Which columns of an admin table are on screen, cached in localStorage.
 *
 * Companion to `createTableFiltersStore`: that one owns what the table queries, this one owns what
 * it shows. Both are per-table stores built from a factory so the rules — never hide a locked
 * column, drop ids from a retired release, keep a new column visible — are written once instead of
 * once per screen.
 *
 * The store holds no JSX and no widths: it is the serializable half. The table keeps its own
 * `Record<TId, string>` of column widths, which the compiler checks for completeness against the
 * same id union.
 *
 * Usage:
 *
 * ```ts
 * // stores/resourceColumnsStore.ts
 * export const RESOURCE_COLUMNS = [
 *   { id: "name", label: "Resource Name", locked: true },
 *   { id: "tenant", label: "Tenant" },
 * ] as const satisfies readonly ColumnDefinition[];
 *
 * export type ResourceColumnId = (typeof RESOURCE_COLUMNS)[number]["id"];
 *
 * export const useResourceColumnsStore = createColumnVisibilityStore<ResourceColumnId>({
 *   name: "admin.resources.columns",
 *   columns: RESOURCE_COLUMNS,
 * });
 * ```
 *
 * ```tsx
 * const isVisible = useResourceColumnsStore((state) => state.isVisible);
 * {isVisible("tenant") && <TableCell>…</TableCell>}
 * ```
 */
export function createColumnVisibilityStore<TId extends string>({
  name,
  columns,
  version = 1,
}: CreateColumnVisibilityStoreOptions<TId>) {
  const knownIds = new Set(columns.map((column) => column.id));
  const lockedIds = new Set(
    columns.filter((column) => column.locked).map((column) => column.id),
  );
  const defaultHidden = columns
    .filter((column) => column.hiddenByDefault && !column.locked)
    .map((column) => column.id);

  /**
   * Rebuilds the hidden set from storage. Two things are thrown away: ids this release no longer
   * has (a stale entry would otherwise sit there forever), and locked ids (a column can become
   * locked after someone hid it, and the table would then render a header with no cells under it).
   */
  const restoreHidden = (stored: unknown): TId[] => {
    if (!Array.isArray(stored)) {
      return [...defaultHidden];
    }

    return stored.filter(
      (id): id is TId =>
        typeof id === "string" &&
        knownIds.has(id as TId) &&
        !lockedIds.has(id as TId),
    );
  };

  return create<ColumnVisibilityState<TId>>()(
    persist(
      (set, get) => ({
        hidden: [...defaultHidden],

        isVisible: (id) => !get().hidden.includes(id),

        toggle: (id) =>
          set((state) => {
            if (lockedIds.has(id)) {
              return state;
            }

            return {
              hidden: state.hidden.includes(id)
                ? state.hidden.filter((hiddenId) => hiddenId !== id)
                : [...state.hidden, id],
            };
          }),

        showAll: () => set({ hidden: [] }),

        reset: () => set({ hidden: [...defaultHidden] }),

        hiddenCount: () => get().hidden.length,
      }),
      {
        name,
        version,
        storage: createJSONStorage(() => safeLocalStorage),

        // Dropping the old state IS the migration, as in createTableFiltersStore. Declaring it
        // stops zustand logging console.error on every load once the version moves.
        migrate: () => undefined,

        partialize: (state) => ({ hidden: state.hidden }),

        merge: (persisted, current) => ({
          ...current,
          hidden: restoreHidden(
            (persisted as { hidden?: unknown } | undefined)?.hidden,
          ),
        }),
      },
    ),
  );
}
